import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { SettingValues } from '@tbbt/shared';
import { Container, Eyebrow, Section } from '@/components/ui';
import { useSite } from '@/app/site-context';
import { mediaUrl } from '@/lib/media';
import { cn } from '@/lib/cn';

/**
 * The team's bios, one at a time, on a timer — and the destination for a click on any
 * card in the grid above.
 *
 * The grid says who works here; this says what they say about the work. The two used to be
 * one thing: every card printed its own bio at 13px, which made the grid a wall of small
 * grey paragraphs and stretched every card to the height of the longest. Splitting them
 * lets the card be a card and gives the words a width and a size somebody will read.
 *
 * It used to rotate on `RotatingArtwork`'s seven-second clock, for one house rhythm. That
 * read as slow here: this is the band people arrive at by clicking a face, and seven
 * seconds per person across ten people is over a minute to get round the team. It now has
 * its own, shorter dwell, and the active dot fills as it runs so the next change is never a
 * surprise.
 *
 * Differences from that component, all because this one carries text rather than
 * decoration:
 *
 * **It can be driven.** Artwork that changes itself is decorative and needs no controls; a
 * quote a visitor is halfway through is not. There are previous/next buttons, dots, and
 * the cards above — and any of them restarts the dwell rather than letting the next
 * automatic advance fire a moment later, which is the most irritating possible response to
 * "I want to read this one".
 *
 * **It pauses.** Pointer over it or keyboard focus inside it stops the clock, because both
 * mean somebody is reading or is about to follow a link.
 *
 * **Every slide stays mounted, stacked in one grid cell.** The bios differ in length by a
 * factor of two, so the band is sized to the longest once and never changes height.
 * Rendering only the active slide would make the section jump on every change and drag
 * the page under the reader's cursor.
 *
 * **The change has a direction.** The outgoing slide slips away and fades in a quarter of
 * a second; the incoming quote slides in from the side it is coming from, and the portrait
 * opens out of its own circle. Forward comes from the right, back from the left, so the
 * arrows feel like they move a strip rather than swap a picture. The keyframes live in
 * `index.css` under `.voice-in` and `.voice-out`.
 *
 * Under `prefers-reduced-motion: reduce` nothing advances on its own, the global rule in
 * `index.css` collapses the keyframes to an instant cut, and the scroll is instant rather
 * than smooth; the buttons still work, so the content stays reachable rather than hidden
 * behind motion the visitor asked us not to produce.
 */

/**
 * Start of one slide to the start of the next.
 *
 * Down from seven seconds, which felt slow. Short enough to keep the band moving, and the
 * rotation pauses under the pointer, so anybody reading a longer bio keeps it on screen.
 */
const ROTATION_MS = 5000;

/**
 * How long the portrait stays lit after a card is clicked.
 *
 * Long enough to still be lit when the smooth scroll finishes — otherwise the confirmation
 * that "this is the person you picked" has already faded by the time the band is on
 * screen, and the visitor arrives at an anonymous slide.
 */
const FLASH_MS = 1600;

type TeamMember = SettingValues['team']['members'][number];

/** A request from the grid above to show one person. */
export interface TeamVoiceFocus {
  /** The member's `name`, which is what the cards hand back. */
  name: string;
  /**
   * Changes on every request, including a repeat of the same person.
   *
   * Without it, clicking the same card twice would not re-run the effect — the visitor
   * would scroll away, click that person again and nothing would happen.
   */
  nonce: number;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Scroll so `section` sits in the middle of the part of the window a reader can see.
 *
 * Not `scrollIntoView({ block: 'center' })`, which centres against the whole viewport and
 * knows nothing about the fixed nav pill over the top of it — the band landed low, with a
 * strip of the white section above still showing and its dots against the bottom edge,
 * and the reader had to nudge the page to see it whole. This centres in the space *below*
 * the nav instead. When the section is taller than that space, its content is centred
 * instead of its padding, and failing that the content's top sits just under the nav, so
 * the heading is never the part that gets cut off.
 *
 * The header tucks itself away while the page scrolls down and returns on the way up, so
 * where it will be *after* this scroll depends on the direction. A scroll down centres in
 * the whole window, because the nav will have slid out of it by the time the band lands;
 * a scroll up centres under the nav, because it will have come back.
 */
function scrollToCentre(section: HTMLElement, content: HTMLElement): void {
  // `offsetHeight` rather than the box: it ignores the header's slide, so this is the
  // height the nav takes when it shows, whether or not it is showing right now. Measured,
  // not a constant, because the pill is shorter on a phone than on a desktop.
  const navHeight = document.querySelector('header')?.offsetHeight ?? 0;

  const place = (navBottom: number): number => {
    const visible = window.innerHeight - navBottom;
    const sectionBox = section.getBoundingClientRect();
    const box = sectionBox.height <= visible ? sectionBox : content.getBoundingClientRect();
    const gap = Math.max(0, (visible - box.height) / 2);
    return Math.max(0, window.scrollY + box.top - navBottom - gap);
  };

  const underNav = place(navHeight);
  const top = underNav > window.scrollY ? place(0) : underNav;

  window.scrollTo({ top, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
}

/**
 * Which slide is showing, which one is on its way out, and which way the strip is moving.
 *
 * One object rather than three `useState`s because they change together and only make
 * sense together: a direction without the slide it moved from animates the wrong way.
 */
interface SlideState {
  index: number;
  /** The slide that was showing before this one; `null` until the first change. */
  previous: number | null;
  /** `1` moves forward (enters from the right), `-1` moves back. */
  direction: 1 | -1;
}

export function TeamVoices({ focus }: { focus?: TeamVoiceFocus | null }): ReactNode {
  const { settings } = useSite();
  const team = settings.team;

  /*
    Only people who wrote something. A card with no bio is fine in the grid — it still
    carries a name, a title and a way to reach them — but a quote slide with no quote is
    just a portrait and a caption, and it would sit in the rotation as a dead beat.
  */
  const voices = [...team.members]
    .filter((member) => member.bio.trim().length > 0)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const [slide, setSlide] = useState<SlideState>({ index: 0, previous: null, direction: 1 });
  const [paused, setPaused] = useState(false);
  const [flash, setFlash] = useState(false);
  const bandRef = useRef<HTMLDivElement>(null);

  const count = voices.length;
  const active = slide.index;

  /*
    Every way of changing the slide comes through here, so each one records where it came
    from and animates the right way. Choosing the slide that is already showing is a no-op
    rather than a replay of its entrance.

    Plain functions, not `useCallback`: the React Compiler is on in this project and
    memoises for us, and a manual one here conflicts with what it infers.
  */
  const show = (index: number, direction: 1 | -1): void =>
    setSlide((current) =>
      index === current.index ? current : { index, previous: current.index, direction },
    );
  // Modulo with a `+ count` so stepping back from the first wraps to the last rather than
  // producing -1.
  const go = (delta: 1 | -1): void =>
    setSlide((current) => ({
      index: (current.index + delta + count) % count,
      previous: current.index,
      direction: delta,
    }));
  // A dot has no arrow on it, so its direction is where it sits relative to the current one.
  const jump = (index: number): void => show(index, index > active ? 1 : -1);

  /*
    Resolved during render rather than inside the effect below, so that effect depends on
    two primitives instead of on `focus` and `voices`. `voices` is rebuilt every render, so
    depending on it would re-run the scroll on every unrelated re-render — the page would
    yank itself back to this band while the visitor was reading something else.
  */
  const focusIndex = focus ? voices.findIndex((member) => member.name === focus.name) : -1;
  const focusNonce = focus?.nonce ?? 0;

  /*
    Adjusted during render rather than synced in an effect.

    This is React's documented way to react to a changed prop — a render-phase update that
    re-renders before anything is painted. Doing it in an effect would set state after the
    browser had already shown the previous slide, so a click on a card would paint the old
    person for a frame and then swap.

    A name with no slide leaves `handledNonce` alone, so the request stays unhandled and a
    later, valid one is not swallowed.
  */
  const [handledNonce, setHandledNonce] = useState(0);
  if (focusNonce !== handledNonce && focusIndex >= 0) {
    setHandledNonce(focusNonce);
    show(focusIndex, focusIndex > active ? 1 : -1);
    setFlash(true);
  }

  // Moving the page is a DOM side effect, so that half stays in an effect — keyed on the
  // request actually taken up, not on the one that arrived.
  useEffect(() => {
    if (handledNonce === 0) return;

    const band = bandRef.current;
    const section = band?.closest('section');
    if (band && section) scrollToCentre(section, band);

    const id = window.setTimeout(() => setFlash(false), FLASH_MS);
    return () => window.clearTimeout(id);
  }, [handledNonce]);

  /*
    A timeout keyed on `active` rather than a repeating interval.

    The effect re-runs whenever the active slide changes — from a click on a card above as
    readily as from the timer — so every slide gets the full dwell measured from the moment
    it appeared. An interval would keep its own schedule and could advance a slide the
    visitor had just chosen, a fraction of a second after they chose it.
  */
  useEffect(() => {
    if (count < 2 || paused || prefersReducedMotion()) return;

    const id = window.setTimeout(
      () =>
        setSlide((current) => ({
          index: (current.index + 1) % count,
          previous: current.index,
          direction: 1,
        })),
      ROTATION_MS,
    );
    return () => window.clearTimeout(id);
  }, [active, count, paused]);

  // Whether the active dot's progress bar is running. It mirrors the timer's own guard so
  // the bar never fills towards a change that is not going to happen.
  const running = count > 1 && !paused && !prefersReducedMotion();

  if (count === 0) return null;

  return (
    /*
      Near-black rather than the brand purple. At full width the purple competed with the
      portraits and the type instead of framing them — see `--color-ink-deep`.
    */
    <Section className="bg-ink-deep" id="in-their-own-words">
      <Container pad="gutter">
        <div ref={bandRef}>
          <div className="mx-auto mb-10 max-w-[760px] text-center md:mb-14">
            <Eyebrow className="mb-3 text-secondary-container">In their own words</Eyebrow>
            <h2 className="text-[30px] font-extrabold leading-tight text-white md:text-[38px]">
              The people running your search.
            </h2>
          </div>

          <div
            className="relative"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            /*
              `focus`/`blur` do not bubble; their `focusin`/`focusout` equivalents do, which
              is what React's `onFocus`/`onBlur` actually bind. So tabbing to the LinkedIn
              link inside a slide pauses the rotation, which is the point.
            */
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
          >
            {/*
              The slides, stacked in a single grid cell so the band is as tall as the
              longest bio and never resizes mid-rotation.
            */}
            <div
              className="grid"
              // Read by the keyframes, which flip their offsets with it.
              style={{ '--voice-dir': slide.direction } as CSSProperties}
            >
              {voices.map((member, index) => (
                <VoiceSlide
                  key={member.name}
                  member={member}
                  phase={
                    index === active ? 'active' : index === slide.previous ? 'leaving' : 'idle'
                  }
                  // No entrance on first paint: the band starts well below the fold, and an
                  // animation nobody sees only delays the first slide being ready.
                  animate={slide.previous !== null}
                  isFlashing={flash && index === active}
                />
              ))}
            </div>

            {count > 1 ? (
              <>
                {/*
                  Below the quote on small screens, pinned to the outer edges from `lg` up
                  where there is room beside it. One set of buttons either way — a second,
                  hidden set would be two things to keep in step and two for a screen
                  reader to find.
                */}
                <div className="mt-10 flex items-center justify-center gap-4 lg:mt-0">
                  <CarouselButton
                    label="Previous team member"
                    onClick={() => go(-1)}
                    className="lg:absolute lg:left-0 lg:top-1/2 lg:-translate-y-1/2"
                  >
                    <ChevronLeft className="size-6" aria-hidden="true" />
                  </CarouselButton>

                  <div className="flex items-center gap-2 lg:hidden">
                    <Dots count={count} active={active} running={running} onSelect={jump} />
                  </div>

                  <CarouselButton
                    label="Next team member"
                    onClick={() => go(1)}
                    className="lg:absolute lg:right-0 lg:top-1/2 lg:-translate-y-1/2"
                  >
                    <ChevronRight className="size-6" aria-hidden="true" />
                  </CarouselButton>
                </div>

                <div className="mt-8 hidden items-center justify-center gap-2 lg:flex">
                  <Dots count={count} active={active} running={running} onSelect={jump} />
                </div>
              </>
            ) : null}
          </div>
        </div>
      </Container>
    </Section>
  );
}

/**
 * One person's slide.
 *
 * `aria-hidden` and `pointer-events-none` travel together on the inactive ones: without
 * the first a screen reader reads all ten bios as one run-on passage, and without the
 * second the invisible slide on top still swallows clicks meant for the visible one.
 *
 * The motion is classes, not inline transitions. Keyframes always start from their first
 * frame, so the incoming slide enters from the correct side however it was last left — a
 * transition would animate from wherever the slide happened to be parked.
 */
function VoiceSlide({
  member,
  phase,
  animate,
  isFlashing,
}: {
  member: TeamMember;
  phase: 'active' | 'leaving' | 'idle';
  animate: boolean;
  isFlashing: boolean;
}): ReactNode {
  const isActive = phase === 'active';

  return (
    <figure
      // Same cell as every sibling, so they overlap instead of stacking vertically.
      className={cn(
        'col-start-1 row-start-1',
        isActive ? 'opacity-100' : 'pointer-events-none opacity-0',
        animate && isActive ? 'voice-in' : null,
        animate && phase === 'leaving' ? 'voice-out' : null,
      )}
      aria-hidden={!isActive}
    >
      <div className="grid items-center gap-10 md:grid-cols-[1fr_auto] md:gap-14 lg:px-16">
        <blockquote className="order-2 md:order-1">
          <div className="voice-quote">
            <span
              aria-hidden="true"
              className="mb-2 block text-[56px] font-black leading-none text-secondary-container/70"
            >
              &ldquo;
            </span>
            <p className="text-[17px] leading-[1.75] text-white/85 md:text-[19px]">{member.bio}</p>
          </div>
          <figcaption className="voice-caption mt-6 text-[15px] font-bold text-white">
            &mdash; {member.name}
            {member.title ? (
              <span className="font-medium text-secondary-container">, {member.title}</span>
            ) : null}
          </figcaption>
        </blockquote>

        <div className="voice-portrait order-1 mx-auto md:order-2">
          <div
            className={cn(
              'size-[200px] overflow-hidden rounded-full border-4 md:size-[260px]',
              // The confirmation that a clicked card landed on the right person. A ring
              // and a nudge in scale, held past the end of the smooth scroll.
              'transition-all duration-500 motion-reduce:transition-none',
              isFlashing
                ? 'scale-105 border-secondary-container shadow-[0_0_0_8px_rgba(177,136,255,0.18)]'
                : 'border-white/10',
            )}
          >
            {member.photoMediaId ? (
              <img
                // `medium` (800px) rather than `thumb` (320px): the portrait is 260px, so
                // a 2x display needs 520 and the thumb rendition visibly softens.
                src={mediaUrl(member.photoMediaId, 'medium')}
                alt={member.name}
                loading="lazy"
                className="size-full object-cover"
              />
            ) : (
              <span
                aria-hidden="true"
                className="grid size-full place-items-center bg-gradient-to-br from-primary-strong to-primary text-5xl font-semibold text-white"
              >
                {member.name
                  .split(/\s+/)
                  .slice(0, 2)
                  .map((part) => part[0]?.toUpperCase() ?? '')
                  .join('')}
              </span>
            )}
          </div>
        </div>
      </div>
    </figure>
  );
}

function CarouselButton({
  label,
  onClick,
  className,
  children,
}: {
  label: string;
  onClick: () => void;
  className?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(
        'grid size-11 place-items-center rounded-full border border-white/20 text-white/70',
        'transition-colors hover:border-white/50 hover:text-white',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary-container',
        className,
      )}
    >
      {children}
    </button>
  );
}

/**
 * Position markers, and a way to jump.
 *
 * Buttons rather than decoration: they say how many people are in the rotation and which
 * one is showing, which the arrows alone do not.
 */
function Dots({
  count,
  active,
  running,
  onSelect,
}: {
  count: number;
  active: number;
  /** Whether the timer is counting down, in which case the active dot fills with it. */
  running: boolean;
  onSelect: (index: number) => void;
}): ReactNode {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <button
          key={index}
          type="button"
          onClick={() => onSelect(index)}
          aria-label={`Show team member ${index + 1} of ${count}`}
          aria-current={index === active}
          className={cn(
            'relative h-2 overflow-hidden rounded-full transition-all duration-300',
            index === active ? 'w-8 bg-white/25' : 'w-2 bg-white/30 hover:bg-white/60',
          )}
        >
          {index === active ? (
            <span
              /*
                Remounted for every slide and on every pause and resume, which restarts the
                fill. The timer restarts its full dwell at the same moments, so the bar
                reaching the end and the slide changing are the same instant.
              */
              key={`${active}-${running}`}
              aria-hidden="true"
              className={cn(
                'absolute inset-y-0 left-0 rounded-full bg-secondary-container',
                running ? 'voice-progress' : 'w-full',
              )}
              style={running ? { animationDuration: `${ROTATION_MS}ms` } : undefined}
            />
          ) : null}
        </button>
      ))}
    </>
  );
}
